import { CloudOff, Loader2, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { markApiDown, markApiHealthy, probeApiReachable } from '@/lib/api/apiHealth'

type OfflineFallbackProps = {
  title?: string
  description?: string
  onRetry?: () => void
}

/**
 * Full-section fallback rendered on REST-only screens when the API is
 * unreachable. Use this on screens that cannot function from local data
 * (billing, profile, invites). For mixed screens prefer disabling individual
 * actions instead.
 */
export function OfflineFallback({
  title = "You're offline",
  description = "This page needs a connection to load. Reconnect and try again.",
  onRetry,
}: OfflineFallbackProps) {
  const [isChecking, setIsChecking] = useState(false)

  const handleRetry = async () => {
    setIsChecking(true)
    try {
      const reachable = await probeApiReachable()
      if (reachable) {
        markApiHealthy()
        onRetry?.()
      } else {
        markApiDown()
      }
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <CloudOff className="h-6 w-6 text-muted-foreground" />
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="max-w-sm text-xs text-muted-foreground">{description}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRetry}
        disabled={isChecking}
      >
        {isChecking ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <RefreshCw className="h-3 w-3" />
        )}
        Retry
      </Button>
    </div>
  )
}
